import { api, qs } from "./base";
import type { ListResponse, RepoDetail, RepoRow } from "@/common/types";

export function listRepos(): Promise<ListResponse<RepoRow>> {
  return api.get<ListResponse<RepoRow>>("/repos");
}

/** Per-repo open-vulnerability counts by severity (repos list overview). */
export interface RepoSeveritySummary {
  repo: string;
  CRITICAL: number;
  HIGH: number;
  MEDIUM: number;
  LOW: number;
  total: number;
  /** Epoch seconds of the last rollup that produced these counts (staleness hint). */
  countsAt?: number;
}

export function severitySummary(): Promise<ListResponse<RepoSeveritySummary>> {
  return api.get<ListResponse<RepoSeveritySummary>>("/repos/severity-summary");
}

export function getRepo(repo: string): Promise<RepoDetail> {
  return api.get<RepoDetail>(`/repos/detail${qs({ repo })}`);
}

// -- Bitbucket discovery (onboarding) --

export interface Workspace {
  slug: string;
  name?: string;
  uuid?: string;
}
export interface BitbucketRepo {
  slug: string;
  name?: string;
  mainBranch?: string;
  managed?: boolean;
}

export function listWorkspaces(): Promise<ListResponse<Workspace>> {
  return api.get<ListResponse<Workspace>>("/bitbucket/workspaces");
}

export function listBitbucketRepos(
  workspace: string,
): Promise<ListResponse<BitbucketRepo>> {
  return api.get<ListResponse<BitbucketRepo>>(
    `/bitbucket/repos${qs({ workspace })}`,
  );
}

// -- AWS stack discovery (guided onboarding) --

export interface CfnStack {
  stackName: string;
  stackId: string; // the ARN
  status: string;
}
export interface CfnResource {
  logicalId: string;
  type: string; // e.g. AWS::ECR::Repository, AWS::Lambda::Function
  physicalId: string;
}

export function listStacks(region: string): Promise<ListResponse<CfnStack>> {
  return api.get<ListResponse<CfnStack>>(`/aws/stacks${qs({ region })}`);
}

export function listStackResources(
  stackArn: string,
  region: string,
): Promise<ListResponse<CfnResource>> {
  return api.get<ListResponse<CfnResource>>(
    `/aws/stack-resources${qs({ stackArn, region })}`,
  );
}

// -- onboard (POST /repos) --

export interface RepoCreate {
  bitbucketWorkspace: string;
  bitbucketRepo: string;
  defaultBranch?: string;
  policy?: string;
  enabled?: boolean;
  ecrRepoNames?: string[];
  owners?: string[];
  notificationEmails?: string[];
  aiContextRoot?: string;
  cloudformationStackArns?: string[];
}

export function createRepo(actor: string, body: RepoCreate) {
  return api.post("/repos", { actor, ...body });
}

/** Patchable config-as-data fields (allowlisted server-side). */
export interface RepoPatch {
  enabled?: boolean;
  policy?: string;
  defaultBranch?: string;
  owners?: string[];
  notificationEmails?: string[];
  component_hint?: string;
  /** Typed monitored-resource keys: "ecr:<name>" | "lambda:<fn>". Replaces ecrRepoNames. */
  monitoredResources?: string[];
  /** @deprecated legacy ECR-only field; still accepted server-side and converted to ecr:<name>. */
  ecrRepoNames?: string[];
  branchPattern?: string;
  exceptionBranchPattern?: string;
  ciEnabled?: boolean;
  requireValidation?: boolean;
  aiContextRoot?: string;
  cloudformationStackArns?: string[];
}

export function patchRepo(repo: string, actor: string, updates: RepoPatch) {
  return api.patch(`/repos${qs({ repo })}`, { actor, ...updates });
}

/** Hard offboard: deletes the repos config row + its ecr_map rows (destructive). */
export function offboardRepo(repo: string, actor: string) {
  return api.del(`/repos${qs({ repo })}`, { actor });
}

export interface PullFindingsResult {
  ok: boolean;
  repo: string;
  pulled: number;
  byRegion?: Record<string, number>;
  note?: string;
}

/**
 * On-demand backfill: read the repo's CURRENT active Inspector findings and re-enqueue them
 * through the normal ingest pipeline. Closes the event-driven blind spot (pre-existing vuln
 * debt that never flows in until Inspector next emits). Idempotent — dedup handles repeats.
 */
export function pullFindings(
  repo: string,
  actor: string,
): Promise<PullFindingsResult> {
  return api.post<PullFindingsResult>(`/repos/pull-findings${qs({ repo })}`, {
    actor,
  });
}
